import type { Stack } from 'expo-router'
import type { RouteInterface } from '@/src/common/enums/routes'
import type { useMesaParameterStore } from '@/src/services/Statsig/useMesaParameterStore'
import { Updates } from './updates'
import { CardPayments } from './card-payments'
import { Rewards } from './rewards'
import { PointsToCashForMortgage } from './points-to-cash-for-mortgage'
export * as Mortgage from './mortgage'
import { Notifications } from './notifications'
export * as PointsToStatementCredit from './points-to-statement-credit'
export * as CardApplication from './card-application'
export * as MortgageEarn from './mortgage-earn'
export * as PointsToOmni from './points-to-omni'
import * as EsmExperiences from './Experiences'

const ExperiencesMap = {
  CardPayments,
  Notifications,
  PointsToCashForMortgage,
  Rewards,
  Updates,
}

/** @see {@link https://linear.app/mesarewards/issue/MESA-745/fix-deeplinking-for-routes-with-disallowed-characters} */
type StringContainingDisallowedDeepLinkCharacter = `${string}${
  | '\\'
  // | '/'
  | '*'
  | '!'
  | '@'
  | '#'
  | '?'
  | '$'
  | '^'
  | '&'
  | '~'
  | '`'
  | '='
  | '+'
  | "'"
  | '"'
  | ';'
  | ':'
  | '>'
  | '<'
  | '('
  | ')'}${string}`

type ExperiencesKeys = keyof typeof ExperiencesMap
type ExperiencesNavigationRoutes = (typeof ExperiencesMap)[ExperiencesKeys]['Navigation']['Routes']
type ExperiencesScreens = (typeof ExperiencesMap)[ExperiencesKeys]['Screens']
type ExperiencesIcons = (typeof ExperiencesMap)[ExperiencesKeys]['Icons']

type ExperiencesConfig = {
  // Not all experiences utilizes statsig
  Statsig?: {
    Store?: {
      [key in `use${string}ParameterStore`]: () => ReturnType<typeof useMesaParameterStore>
    }
    FeatureGate?: { [key: string]: string }
  }
  Screens: ExperiencesScreens
  Navigation: {
    Layout: (props: { ExpoStackProps: React.ComponentProps<typeof Stack> }) => React.JSX.Element
    Routes: {
      [key in keyof ExperiencesNavigationRoutes]: RouteInterface & // if the route is available for deep linking
        (ExperiencesNavigationRoutes[key]['availableForDeepLinking'] extends true
          ? // and the route contains a disallowed deep link character
            ExperiencesNavigationRoutes[key]['route'] extends StringContainingDisallowedDeepLinkCharacter
            ? // then require aliases to be present
              ExperiencesNavigationRoutes[key] extends { aliases: infer Aliases }
              ? // and for each alias to be a string
                Aliases extends string[]
                ? // and for each alias to not contain a disallowed deep link character
                  Aliases[number] extends StringContainingDisallowedDeepLinkCharacter
                  ? never
                  : { aliases: ExperiencesNavigationRoutes[key]['aliases'] }
                : never
              : never
            : object
          : object)
    }
  }
  // Not all experiences utilizes icons
  Icons?: ExperiencesIcons
}

export const Experiences = { ...ExperiencesMap, ...EsmExperiences } as const satisfies Record<
  ExperiencesKeys,
  ExperiencesConfig
>
